// components/comparison/BiodiversityProtectedChart.jsx
import React, { useMemo, useState, useEffect } from "react";
import NativeAnalysis from "../NativeAnalysis";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  Label,
} from "recharts";

const DASH_POOL = [""];

// Backend puede venir como node.charts.chartbio2 o node.chartbio2
function getChart(comparisonData, pathwayName, chartKey) {
  const node = comparisonData?.[pathwayName];
  return node?.charts?.[chartKey] || node?.[chartKey] || null;
}

function CustomTooltip({ active, payload, label, pathwayIndex }) {
  if (!active || !payload?.length) return null;

  const sorted = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : -Infinity;
    const bv = typeof b.value === "number" ? b.value : -Infinity;
    return bv - av;
  });

  const getDash = (pathway) => {
    if (pathway === "__target__") return "5 5";
    const idx = pathwayIndex?.get(pathway) ?? 0;
    return DASH_POOL[idx % DASH_POOL.length];
  };

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 shadow-md text-[11px]">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>

      {sorted.map((item) => {
        const dash = getDash(item.dataKey);
        const displayName = item.dataKey === "__target__" ? "PA Target (30x30)" : item.name;
        return (
          <div
            key={item.dataKey}
            className="mb-0.5 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2">
              <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
                <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" strokeDasharray={dash} />
              </svg>
              <span className="text-[#7E8081]">{displayName}</span>
            </div>

            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? item.value.toLocaleString("es-ES", { maximumFractionDigits: 2 })
                : item.value}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Legend clickable: ocultar/mostrar trayectorias (series) incluyendo PA Target */
function ClickableLegend({ payload, hiddenKeys, onToggle, pathwayIndex }) {
  if (!payload?.length) return null;

  const getDash = (pathway) => {
    if (pathway === "__target__") return "2 3";
    const idx = pathwayIndex?.get(pathway) ?? 0;
    return DASH_POOL[idx % DASH_POOL.length];
  };

  // No filtrar el target, sino hacerlo interactivo
  const processedPayload = payload.map((entry) => ({
    ...entry,
    displayLabel: entry.dataKey === "__target__" ? "PA Target (30x30)" : entry.value,
  }));

  return (
    <div className="mb-2 flex flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {processedPayload.map((entry) => {
        const isHidden = hiddenKeys.has(entry.dataKey);
        const dash = getDash(entry.dataKey);
        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry.dataKey)}
            className={`flex items-center gap-2 ${isHidden ? "opacity-40" : ""}`}
            title="Click para mostrar/ocultar"
          >
            <span className="text-[12px]">{entry.displayLabel}</span>
            <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
              <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color} strokeWidth="2" strokeDasharray={dash} />
            </svg>
          </button>
        );
      })}
    </div>
  );
}

export default function BiodiversityProtectedChart({
  comparisonData,
  selectedPathways,
  indicator,
  getColorByName,
  xLabel = "Year",
  yLabel = "Share / Area", // pon aquí la unidad real si la sabes
}) {
  // índice por pathway para dashes estables
  const pathwayIndex = useMemo(() => {
    const map = new Map();
    (selectedPathways || []).forEach((p, i) => map.set(p, i));
    return map;
  }, [selectedPathways]);

  const dashForPathway = (p) => {
    const idx = pathwayIndex.get(p) ?? 0;
    return DASH_POOL[idx % DASH_POOL.length];
  };

  // ✅ ocultar/mostrar series (trayectorias)
  const [hiddenPathways, setHiddenPathways] = useState(() => new Set());
  const togglePathway = (p) => {
    setHiddenPathways((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const { rows, keys, hasTargetData } = useMemo(() => {
    if (!indicator || !selectedPathways?.length) return { rows: [], keys: [], hasTargetData: false };

    const first = selectedPathways[0];
    const base = getChart(comparisonData, first, "chartbio2");
    const labels = base?.labels || [];

    // Obtener datos de PA Target (30x30) siempre
    const targetDs = base?.datasets?.find((d) => d.label === "PA Target (30x30)");
    const hasTargetData = !!targetDs;

    const rows = labels.map((year, idx) => {
      const row = { year };

      for (const p of selectedPathways) {
        const c = getChart(comparisonData, p, "chartbio2");
        if (!c?.labels?.length) continue;

        let value = null;
        
        // Si el indicador es "PA Total", calcular suma de PA Forests + PA Otherland + PA Misc
        if (indicator === "PA Total") {
          const forests = c.datasets?.find((d) => d.label === "PA Forests")?.data?.[idx];
          const otherland = c.datasets?.find((d) => d.label === "PA Otherland")?.data?.[idx];
          const misc = c.datasets?.find((d) => d.label === "PA Misc")?.data?.[idx];
          
          // Si al menos uno es un número, sumar todos (los undefined se tratan como 0)
          if (typeof forests === "number" || typeof otherland === "number" || typeof misc === "number") {
            value = (typeof forests === "number" ? forests : 0) + 
                    (typeof otherland === "number" ? otherland : 0) + 
                    (typeof misc === "number" ? misc : 0);
          }
        } else {
          const ds = c.datasets?.find((d) => d.label === indicator);
          value = ds?.data?.[idx] ?? null;
        }

        row[p] = value;
      }
      
      // Agregar datos de target siempre
      if (hasTargetData && targetDs) {
        row["__target__"] = targetDs.data?.[idx] ?? null;
      }
      
      return row;
    });

    const keys = selectedPathways.filter((p) => {
      const c = getChart(comparisonData, p, "chartbio2");
      if (indicator === "PA Total") {
        return c?.datasets?.some((d) => ["PA Forest", "PA Otherland", "PA Misc"].includes(d.label));
      }
      return c?.datasets?.some((d) => d.label === indicator);
    });

    return { rows, keys, hasTargetData };
  }, [comparisonData, selectedPathways, indicator]);

  const [analysis, setAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    if (!rows?.length) return;

    // Only generate GPT descriptions when there are at least two pathways to compare
    if (!selectedPathways || selectedPathways.length < 2) {
      setAnalysis("Please select at least two pathways to compare analysis.");
      return;
    }
    
    let isMounted = true;
    const fetchAnalysis = async () => {
      setLoadingAnalysis(true);
      try {
        const payload = {
          context: "biodiversity_protected",
          dataset: rows,
        };
        const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/chat/fablegpt-comparison`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error("Analysis API error");
        const resData = await response.json();
        if (isMounted) setAnalysis(resData.analysis?.replace(/_/g, " ") || "No analysis available.");
      } catch (error) {
        console.error("Error fetching comparison analysis:", error);
        if (isMounted) setAnalysis("Cannot load analysis at this time.");
      } finally {
        if (isMounted) setLoadingAnalysis(false);
      }
    };
    
    fetchAnalysis();

    return () => { isMounted = false; };
  }, [rows, selectedPathways]);

  if (!indicator) {
    return (
      <div className="rounded-xl bg-slate-50 p-6 ring-1 ring-slate-200 text-sm text-slate-700">
        Select a protected-area indicator.
      </div>
    );
  }

  if (!rows?.length || !keys?.length) {
    return (
      <div className="rounded-xl bg-slate-50 p-6 ring-1 ring-slate-200 text-sm text-slate-700">
        No data for Protected areas: {indicator}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-white px-6 py-5 ring-1 ring-[#E3E5E6] shadow-sm">
      <div className="text-[14px] font-semibold text-[#101010] mb-3">
        Evolution of Protected Areas — {indicator}
      </div>

      <div style={{ width: "100%", height: 440 }}>
        <ResponsiveContainer>
          <LineChart
            data={rows}
            margin={{ top: 30, right: 40, left: 20, bottom: 40 }}
          >
            <CartesianGrid
              stroke="#F1F3F4"
              strokeDasharray="3 3"
              vertical={false}
            />

            <XAxis
              dataKey="year"
              tick={{ fill: "#7E8081", fontSize: 12 }}
              tickMargin={12}
              axisLine={{ stroke: "#E3E5E6" }}
              tickLine={false}
            >
              <Label
                value={xLabel}
                position="insideBottom"
                offset={-30}
                style={{ fill: "#98999A", fontSize: 14, fontWeight: 600 }}
              />
            </XAxis>

            <YAxis
              tick={{ fill: "#7E8081", fontSize: 12 }}
              tickMargin={10}
              axisLine={{ stroke: "#E3E5E6" }}
              tickLine={false}
            >
              <Label
                value={yLabel}
                angle={-90}
                position="insideLeft"
                style={{ fill: "#98999A", fontSize: 14, fontWeight: 600 }}
              />
            </YAxis>

            <Tooltip content={<CustomTooltip pathwayIndex={pathwayIndex} />} />

            {/* ✅ legend arriba + clickable */}
            <Legend
              verticalAlign="top"
              align="center"
              wrapperStyle={{ paddingBottom: 8 }}
              content={(props) => (
                <ClickableLegend
                  {...props}
                  hiddenKeys={hiddenPathways}
                  onToggle={togglePathway}
                  pathwayIndex={pathwayIndex}
                />
              )}
            />

            {keys.map((p) => (
              <Line
                key={p}
                type="monotone"
                dataKey={p}
                name={p}
                stroke={getColorByName(p)}
                strokeDasharray={dashForPathway(p)}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
                connectNulls
                isAnimationActive={false}
                hide={hiddenPathways.has(p)}
              />
            ))}

            {/* PA Target (30x30) - línea fija en rojo */}
            {hasTargetData && (
              <Line
                type="monotone"
                dataKey="__target__"
                name="PA Target (30x30)"
                stroke="#ff0000"
                strokeDasharray="5 5"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
                connectNulls
                isAnimationActive={false}
                hide={hiddenPathways.has("__target__")}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="w-full min-h-[100px] mt-4">
        {loadingAnalysis ? (
          <div className="flex items-center gap-2 text-[#7E8081] text-sm animate-pulse">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Generating comparative analysis...
          </div>
        ) : (
          <div className="group relative">
            <NativeAnalysis content={analysis} />
          </div>
        )}
      </div>
    </div>
  );
}